import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchProdutoDetalhe } from '../../services/api';
import Loading from '../../components/Loading/Loading';
import ProductImage from '../../components/ProductImage/ProductImage';
import ProductObservations from '../../components/ProductObservations/ProductObservations';
import AnexarFotoProduto, { versaoFoto } from '../../components/AnexarFotoProduto/AnexarFotoProduto';
import HistoricoComprasDetalhado from '../../components/HistoricoComprasDetalhado/HistoricoComprasDetalhado';
import { useSugestaoCompra } from '../../hooks/useSugestaoCompra';
import styles from './ProductDetail.module.css';

// ─── Hero de Sugestão de Compra ───────────────────────────────────────────
const fmtNum = (v) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(v || 0);

function gerarDescricao({ sugestao, calculo, vendas }) {
  if (!calculo) return '';

  const {
    demanda_diaria = 0,
    lead_time = 0,
    lead_time_fonte = '',
    lead_time_aplicado,
    lead_time_confiavel,
    cobertura_dias = 30,
    buffer = 0,
    estoque = 0,
    solicitacoes_abertas = 0,
    necessidade = 0,
    janela_escolhida,
    dias_ativos
  } = calculo;

  const semHistoricoEstoque = !!(dias_ativos?.sem_historico);

  if (sugestao === 0) {
    if (demanda_diaria === 0 && estoque === 0 && (vendas?.dia_365 || 0) === 0) {
      return 'Nenhuma venda registrada nos últimos 12 meses e sem estoque atual. Considere se este item ainda faz parte do mix.';
    }
    if (estoque + solicitacoes_abertas >= necessidade) {
      const diasCobertura = demanda_diaria > 0 ? Math.round((estoque + solicitacoes_abertas) / demanda_diaria) : null;
      return `Estoque atual de ${estoque} un${solicitacoes_abertas > 0 ? ` (+ ${solicitacoes_abertas} em pedido aberto)` : ''} já cobre a necessidade${diasCobertura ? ` — aproximadamente ${diasCobertura} dias de venda no ritmo atual` : ''}. Não é preciso comprar agora.`;
    }
    return `Sem demanda detectada — produto não teve estoque suficiente para vender. Reavalie manualmente antes de fazer pedido.`;
  }

  // ── Trecho da janela: detalha QUAL janela e dias ativos quando disponível
  // Robusto a cache antigo: se não temos janela_escolhida, inferimos qual janela pelo MAX dos brutos.
  const inferirJanela = () => {
    const candidatos = [
      { fonte: '30d',  bruto: vendas?.dia_30  || 0, janela: 30 },
      { fonte: '90d',  bruto: vendas?.dia_90  || 0, janela: 90 },
      { fonte: '365d', bruto: vendas?.dia_365 || 0, janela: 365 },
      { fonte: 'mês corrente',          bruto: vendas?.mes_corrente || 0, janela: new Date().getDate() },
      { fonte: 'mesmo mês ano passado', bruto: vendas?.mesmo_mes_ano_passado || 0, janela: 30 }
    ];
    return candidatos.reduce((a, b) => (b.bruto / Math.max(b.janela, 1) >= a.bruto / Math.max(a.janela, 1) ? b : a));
  };

  let trechoJanela;
  if (janela_escolhida && janela_escolhida.fonte) {
    const fonte = janela_escolhida.fonte;
    const divisor = janela_escolhida.divisor_usado || janela_escolhida.dias_janela || 0;
    const janelaDias = janela_escolhida.dias_janela || 0;
    const ruptura = janela_escolhida.dias_ruptura || 0;
    const brutoPorFonte = {
      '30d':  vendas?.dia_30 || 0,
      '90d':  vendas?.dia_90 || 0,
      '365d': vendas?.dia_365 || 0,
      'mês corrente':         vendas?.mes_corrente || 0,
      'mesmo mês ano passado': vendas?.mesmo_mes_ano_passado || 0
    };
    const bruto = brutoPorFonte[fonte] ?? 0;
    if (!semHistoricoEstoque && ruptura > 0 && (fonte === '30d' || fonte === '90d' || fonte === '365d')) {
      trechoJanela = `últimos ${janelaDias} dias — **${divisor} dias com estoque ativo, ${ruptura} dias em ruptura**: ${bruto} un vendidas`;
    } else {
      trechoJanela = `${fonte}: ${bruto} un vendidas`;
    }
  } else {
    // Cache antigo: infere janela vencedora a partir dos brutos
    const inf = inferirJanela();
    trechoJanela = inf.bruto > 0
      ? `${inf.fonte}: ${inf.bruto} un vendidas`
      : `histórico recente`;
  }

  // ── Trecho do lead time: só conta quando vem do histórico real
  // Mediana global é fallback ingênuo (15d default) — não incluímos no cálculo.
  const leadTimeConfiavelExplicito = (lead_time_confiavel !== undefined) ? lead_time_confiavel : (lead_time_fonte && lead_time_fonte !== 'mediana global');
  const leadTimeAplicadoEfetivo = (lead_time_aplicado !== undefined) ? lead_time_aplicado : (leadTimeConfiavelExplicito ? lead_time : 0);

  const trechoLead = leadTimeConfiavelExplicito && leadTimeAplicadoEfetivo > 0
    ? `Com lead time de **${leadTimeAplicadoEfetivo} dias** (${lead_time_fonte}) e cobertura desejada de **${cobertura_dias} dias**, a necessidade total é **${Math.ceil(necessidade)} un**.`
    : `Usando a cobertura de **${cobertura_dias} dias**, a necessidade total é **${Math.ceil(necessidade)} un**.`;

  return (
    `Sugerimos **${sugestao} unidades** porque o produto vende em média **${fmtNum(demanda_diaria)} un/dia** ` +
    `(calculado a partir de ${trechoJanela}). ` +
    trechoLead + ` ` +
    `Você tem **${estoque} un** em estoque` +
    (solicitacoes_abertas > 0 ? ` e **${solicitacoes_abertas} un** em pedido aberto` : '') +
    `, então falta comprar **${sugestao} un**` +
    (buffer > 0 ? ` (já incluso buffer de segurança de ${buffer} un)` : '') +
    `.`
  );
}

function renderDescricao(texto) {
  // Renderiza **texto** como <strong>
  const partes = texto.split(/\*\*(.+?)\*\*/g);
  return partes.map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>));
}

const SugestaoCompraHero = ({ codigo, fornecedor, loja, estoque, precoCusto, precoVenda }) => {
  const { data, isLoading } = useSugestaoCompra({
    codigo, fornecedor, loja, estoque, precoCusto, precoVenda
  });

  if (isLoading) {
    return (
      <div className={styles.sugestaoHero}>
        <div className={styles.sugestaoHeroLabel}>Sugestão de Compra</div>
        <div className={styles.sugestaoHeroSkeleton} />
      </div>
    );
  }

  if (!data?.success || data.cache_miss || data.sugestao === null || data.sugestao === undefined) {
    return (
      <div className={styles.sugestaoHero}>
        <div className={styles.sugestaoHeroLabel}>Sugestão de Compra</div>
        <div className={styles.sugestaoHeroEmpty}>
          Cálculo ainda não disponível para este produto. Atualiza a cada hora.
        </div>
      </div>
    );
  }

  const { sugestao, calculo = {}, vendas = {}, alertas = [] } = data;
  // Regra nova (2026-07-01): o veredito segue a MEDIANA dos 3 cenários; cache antigo
  // sem cenários cai no texto/fluxo antigo (some sozinho no próximo recálculo horário).
  const cen = calculo.cenarios || null;
  const medianaSug = cen ? (cen.mediana?.sugestao ?? 0) : sugestao;
  const otimSug = cen ? (cen.otimista?.sugestao ?? 0) : 0;
  const ehZero = cen ? medianaSug === 0 : sugestao === 0;
  const semVendas = cen?.motivo_zerado === 'sem_vendas';

  const janela = cen?.janela_dias || 90;
  const corte = cen?.corte_dias || 0;
  const estoqueAtual = calculo.estoque || 0;
  const pedidosAbertos = calculo.solicitacoes_abertas || 0;

  // Uma linha por cenário: valor + explicação do que aquele número assume
  const explicaComo = {
    pessimista: corte > 0
      ? `ignora os ${corte} dias de MAIOR venda — pico/atacado não infla a compra`
      : 'histórico curto — usa a média simples dos dias',
    mediana: corte > 0
      ? `média aparada: descarta os ${corte} dias de maior e os ${corte} de menor venda — a estimativa mais equilibrada`
      : 'histórico curto — usa a média simples dos dias',
    otimista: 'espera vender um pouco mais: usa o ritmo das últimas 2 semanas quando a venda está acelerando'
  };
  const linhasCenarios = cen ? [
    { key: 'pessimista', label: 'Pessimista', cor: '#dc2626', c: cen.pessimista, destaque: false },
    { key: 'mediana', label: 'Mediana', cor: '#a16207', c: cen.mediana, destaque: true },
    { key: 'otimista', label: 'Otimista', cor: '#16a34a', c: cen.otimista, destaque: false }
  ] : [];

  return (
    <div className={`${styles.sugestaoHero} ${ehZero ? styles.zero : ''}`}>
      <div className={styles.sugestaoHeroLabel}>Sugestão de Compra</div>
      <div className={styles.sugestaoHeroValor}>
        {ehZero
          ? (semVendas
              ? 'Sem vendas no trimestre'
              : (otimSug > 0 ? 'Não comprar agora (só no otimista)' : 'Não comprar agora'))
          : `Comprar ${medianaSug} un`}
      </div>

      {cen && !semVendas && (
        <>
          <div style={{ fontSize: '0.83rem', color: '#64748b', margin: '4px 0 10px' }}>
            Base: vendas por dia dos últimos <strong>{janela} dias</strong>
            {' · '}<strong>{estoqueAtual} un</strong> em estoque
            {pedidosAbertos > 0 && <> + <strong>{pedidosAbertos} un</strong> em pedido aberto</>}
            {' '}(descontados da compra)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '6px' }}>
            {linhasCenarios.map(({ key, label, cor, c, destaque }) => {
              const v = c?.sugestao ?? 0;
              const dem = fmtNum(c?.demanda || 0);
              const nec = c?.necessidade_30d || 0;
              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 14px',
                  borderRadius: '8px',
                  background: destaque ? '#fef9c3' : 'rgba(0,0,0,0.03)',
                  border: `1px solid ${destaque ? '#fde047' : 'rgba(0,0,0,0.07)'}`
                }}>
                  <div style={{ minWidth: '86px', fontSize: '0.68rem', fontWeight: 700, color: cor, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {label}{destaque ? ' ★' : ''}
                  </div>
                  <div style={{ minWidth: '72px', fontSize: destaque ? '1.35rem' : '1.15rem', fontWeight: 800, color: v > 0 ? cor : '#94a3b8', lineHeight: 1.1, textAlign: 'right' }}>
                    {v}<span style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', marginLeft: '3px' }}>un</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.45 }}>
                    {explicaComo[key]}: <strong>{dem} un/dia</strong> → <strong>{nec} un</strong> para 30 dias
                    {v > 0
                      ? <> — descontando o que já temos, comprar <strong>{v} un</strong></>
                      : <> — o que já temos cobre os 30 dias, <strong>não comprar</strong></>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {cen && semVendas && (
        <p className={styles.sugestaoHeroDescricao}>
          Nenhuma venda nos últimos 90 dias — sem base para sugerir compra. Reavalie manualmente se este item ainda faz parte do mix.
        </p>
      )}

      {!cen && (
        <p className={styles.sugestaoHeroDescricao}>
          {renderDescricao(gerarDescricao({ sugestao, calculo, vendas }))}
        </p>
      )}
      {alertas.length > 0 && (
        <div className={styles.sugestaoHeroAlertas}>
          {alertas.map((a, i) => {
            const cls = a.nivel === 'critico' ? styles.sugestaoHeroAlertaCritico
                      : a.nivel === 'aviso'   ? styles.sugestaoHeroAlertaAviso
                      : styles.sugestaoHeroAlertaInfo;
            return <div key={i} className={`${styles.sugestaoHeroAlerta} ${cls}`}>{a.msg}</div>;
          })}
        </div>
      )}
    </div>
  );
};

const ProductDetail = () => {
  const { tipo, codigo } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lojaParam = searchParams.get('loja') || '';

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['produto-detalhe', tipo, codigo, lojaParam],
    queryFn: () => fetchProdutoDetalhe(tipo, codigo, lojaParam, versaoFoto(codigo))
  });

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const getMesNome = (mesNum) => {
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return meses[mesNum - 1] || '';
  };

  const getRiscoColor = (risco) => {
    const colors = {
      critico: '#e74c3c',
      alto: '#e67e22',
      medio: '#f39c12',
      baixo: '#27ae60'
    };
    return colors[risco] || '#95a5a6';
  };

  const getTipoRecomendacaoColor = (tipo) => {
    const colors = {
      urgente: '#e74c3c',
      alerta: '#e67e22',
      atencao: '#f39c12',
      oportunidade: '#27ae60',
      sazonalidade: '#004B87',
      info: '#3498db'
    };
    return colors[tipo] || '#95a5a6';
  };

  if (isLoading) return <Loading />;

  if (isError) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>Erro ao carregar produto: {error.message}</p>
          <button onClick={() => navigate(-1)} className={styles.button}>
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const produto = data?.data;
  console.log('Dados recebidos para o produto:', produto); // DEBUG
  const isEcommerce = tipo === 'ecommerce';
  // Qual loja está sendo vista: a rota é /:tipo/:codigo ("loja-fisica"), e a loja
  // real vem do ?loja=. Sem isso, Caxias cairia no calendário do Centro.
  const lojaCalendario = isEcommerce
    ? 'ecommerce'
    : (lojaParam === 'caxias' || tipo === 'caxias' ? 'caxias' : 'centro');

  const getImageSrc = () => {
    if (isEcommerce) {
      return produto.imagemURL;
    } else {
      return produto.imagemBase64;
    };
  };

  const buildEstudoQuedaTexto = () => {
    if (!produto || !produto.analisePreditiva || !produto.historicoVendas || produto.historicoVendas.length === 0) {
      return '';
    }

    const { analisePreditiva, historicoVendas } = produto;

    const mesPico = analisePreditiva.mesPico;
    const tendencia = analisePreditiva.tendencia;
    const ruptura = analisePreditiva.ruptura;

    const mesesOrdenados = [...historicoVendas].sort((a, b) => {
      if (a.ano === b.ano) return a.mes - b.mes;
      return a.ano - b.ano;
    });

    const picoIdx = mesesOrdenados.findIndex(
      (m) => m.mes === mesPico.mes && m.ano === mesPico.ano
    );

    let texto = '';

    if (picoIdx !== -1) {
      const pico = mesesOrdenados[picoIdx];
      const after1 = mesesOrdenados[picoIdx + 1];
      const after2 = mesesOrdenados[picoIdx + 2];

      texto += `O pico de vendas deste produto ocorreu em ${getMesNome(pico.mes)}/${pico.ano}, com ${pico.quantidade} unidades vendidas. `;

      // Intensidade do pico em relação aos demais meses
      const outrosMeses = mesesOrdenados.filter((_, idx) => idx !== picoIdx);
      const mediaOutros = outrosMeses.length > 0
        ? outrosMeses.reduce((acc, v) => acc + (v.quantidade || 0), 0) / outrosMeses.length
        : 0;

      if (mediaOutros > 0) {
        const fatorPico = (pico.quantidade / mediaOutros).toFixed(1);
        texto += `Esse pico representa cerca de ${fatorPico}x a média de vendas dos demais meses analisados. `;
      }

      if (after1 || after2) {
        const ref = pico.quantidade || 1;
        const q1 = after1 ? after1.quantidade : 0;
        const q2 = after2 ? after2.quantidade : 0;
        const p1 = after1 ? ((q1 / ref) * 100).toFixed(1) : null;
        const p2 = after2 ? ((q2 / ref) * 100).toFixed(1) : null;

        if (after1 && after2) {
          texto += `Nos meses seguintes, as vendas caíram para ${q1} un. (${p1}% do pico) em ${getMesNome(after1.mes)}/${after1.ano} e ${q2} un. (${p2}% do pico) em ${getMesNome(after2.mes)}/${after2.ano}. `;
        } else if (after1) {
          texto += `No mês seguinte, as vendas caíram para ${q1} un. (${p1}% do pico) em ${getMesNome(after1.mes)}/${after1.ano}. `;
        }
      }
    }

    if (tendencia && tendencia.descricao) {
      texto += `A tendência geral nos últimos meses é de ${tendencia.descricao.toLowerCase()}. `;
    }

    if (ruptura && ruptura.mensagem) {
      texto += `Em relação ao estoque, o sistema identificou ${ruptura.mensagem.toLowerCase()}. `;
      if (typeof ruptura.diasEstoque === 'number' && ruptura.diasEstoque > 0) {
        texto += `Com o estoque atual, estimamos aproximadamente ${ruptura.diasEstoque} dias de cobertura considerando o ritmo recente de vendas. `;
      }
    }

    if (analisePreditiva.mesPico && analisePreditiva.mesPico.motivosPossiveis && analisePreditiva.mesPico.motivosPossiveis.length > 0) {
      texto += 'Possíveis fatores que explicam o pico incluem: ';
      texto += analisePreditiva.mesPico.motivosPossiveis.join(', ');
      texto += '. ';
    }

    return texto;
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Voltar ao Catálogo
        </button>

        {/* Foto do produto + anexar foto (Loja Física) logo no topo — antes ficava depois do histórico e ninguém achava */}
        {!isEcommerce && (
          <div className={styles.fotoTopo}>
            <ProductImage
              key={getImageSrc() || 'sem-foto'}
              src={getImageSrc()}
              alt={produto.descricao}
              className={styles.fotoTopoImagem}
            />
            <div className={styles.fotoTopoInfo}>
              <span className={styles.codigo}>Código: {produto.codigoInterno}</span>
              <h2 className={styles.fotoTopoTitulo}>{produto.descricao}</h2>
              <AnexarFotoProduto codigo={produto.codigoInterno} />
            </div>
          </div>
        )}

        {/* 1ª informação: Sugestão de Compra com descrição explicando o porquê */}
        {/* Mapeia tipo da URL pro valor que o backend de sugestão entende.
            tipo='loja-fisica' (página agnóstica) → 'centro' como default (loja principal). */}
        <SugestaoCompraHero
          codigo={isEcommerce ? produto.codigo : produto.codigoInterno}
          fornecedor={produto.fornecedor || null}
          loja={isEcommerce ? 'ecommerce' : (tipo === 'caxias' ? 'caxias' : 'centro')}
          estoque={produto.estoque}
          precoCusto={produto.precoCusto}
          precoVenda={isEcommerce ? produto.preco : produto.precoVenda}
        />

        {/* 2ª informação: Detalhamento Mensal (histórico de vendas/reposições por mês com expand pra dias) */}
        <HistoricoComprasDetalhado
          historicoVendas={produto.historicoVendas || []}
          estoque={isEcommerce ? (produto.estoque || 0) : (produto.estoque || 0)}
          mediaMensal={isEcommerce ? (produto.mediaMensal || 0) : (produto.mediaMensal || 0)}
          tipo={tipo}
          variacoes={produto.variacoes || []}
          historicoEntradas={produto.historicoEntradas || []}
          saldosMensais={produto.saldosMensais || {}}
          loja={lojaCalendario}
        />

        <div className={styles.productContainer}>
          <div className={styles.imageSection}>
            <ProductImage
              key={getImageSrc() || 'sem-foto'}
              src={getImageSrc()}
              alt={isEcommerce ? produto.nome : produto.descricao}
              className={styles.image}
            />
          </div>

          <div className={styles.infoSection}>
            <div className={styles.header}>
              <span className={styles.codigo}>
                Código: {isEcommerce ? produto.codigo : produto.codigoInterno}
              </span>
              {produto.margem > 0 && (
                <span className={styles.badge}>{produto.margem}% de margem</span>
              )}
            </div>

            <h1 className={styles.title}>
              {isEcommerce ? produto.nome : produto.descricao}
            </h1>

            {!isEcommerce && produto.descricaoResumida && (
              <p className={styles.descricaoResumida}>
                {produto.descricaoResumida}
              </p>
            )}

            <div className={styles.prices}>
              <div className={styles.priceRow}>
                <span className={styles.label}>Preço de Venda:</span>
                <span className={styles.priceVenda}>
                  {formatarMoeda(isEcommerce ? produto.preco : produto.precoVenda)}
                </span>
              </div>
              <div className={styles.priceRow}>
                <span className={styles.label}>Preço de Custo:</span>
                <span className={styles.precoCusto}>
                  {formatarMoeda(produto.precoCusto)}
                </span>
              </div>
              <div className={styles.priceRow}>
                <span className={styles.label}>Margem de Lucro:</span>
                <span className={styles.margem}>{produto.margem}%</span>
              </div>
            </div>

            {!isEcommerce && produto.codigoBarras && (
              <div className={styles.info}>
                <span className={styles.label}>Código de Barras:</span>
                <span className={styles.value}>{produto.codigoBarras}</span>
              </div>
            )}

            {/* Estudo da Queda de Vendas - focado em loja física */}
            {!isEcommerce && (
              <div className={styles.estudoQuedaSection}>
                <h2 className={styles.sectionTitle}>🧠 Estudo da Queda de Vendas</h2>
                <p className={styles.estudoTexto}>
                  {buildEstudoQuedaTexto()}
                </p>
              </div>
            )}

            <div className={styles.estoque}>
              <span className={styles.label}>Estoque:</span>
              <span
                className={
                  produto.estoque > 0 ? styles.emEstoque : styles.semEstoque
                }
              >
                {produto.estoque > 0
                  ? `${produto.estoque} unidades`
                  : 'Sem estoque'}
              </span>
            </div>

            {/* Estoque ideal sugerido (apenas para loja física) */}
            {!isEcommerce && produto.estoqueIdealSugerido > 0 && (
              <div className={styles.info}>
                <span className={styles.label}>Estoque ideal sugerido:</span>
                <span
                  className={styles.value}
                  title="Calculado com base no consumo médio recente, risco de ruptura e proximidade do mês de pico/sazonalidade."
                >
                  {produto.estoqueIdealSugerido} unidades
                </span>
              </div>
            )}

            {!isEcommerce && produto.fornecedor && (
              <div className={styles.info}>
                <span className={styles.label}>Fornecedor:</span>
                <span className={styles.value}>{produto.fornecedor}</span>
              </div>
            )}
          </div>
        </div>

        {/* Observações e Delegações */}
        <ProductObservations productCode={isEcommerce ? produto.codigo : produto.codigoInterno} />
      </div>
    </div>
  );
};

export default ProductDetail;
